import { useMemo } from "react";
import { getName } from "country-list";
import ISO6391 from "iso-639-1";
import * as yup from "yup";
import each from "lodash/each";
import get from "lodash/get";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import isInteger from "lodash/isInteger";
import isNil from "lodash/isNil";
import isPlainObject from "lodash/isPlainObject";
import isString from "lodash/isString";
import toInteger from "lodash/toInteger";
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT, VIEW_TIME_FORMAT } from "../constants/dateTimeConstants";
import { FIELD_TYPES, FIELD_TYPES_WITHOUT_META_CONFIG_VALUE, SELECT_FIELD_MODES } from "../constants/fieldConstants";
import { REQUIRED_MESSAGES, TYPE_ERROR_MESSAGES } from "../constants/schemaConstants";
import { CONDITION_TYPES } from "../components/MenuAdmin/Forms/FormFieldCondition";
import { isAfter, isSameOrAfter, isSameOrBefore, parseDateTime } from "./dateTimeUtils";
import getDecimalsCount from "./getDecimalsCount";
import { FIELD_TYPES_ARRAY } from "./getFieldDefaultValue";
import isBlank from "./isBlank";
import { schemaNullable, schemaPhone } from "./yupUtils";

const UPLOAD_TYPES = [FIELD_TYPES.file, FIELD_TYPES.image];

/**
 * For a field which has a condition specified (i.e. show or hide the field based on the value of another field), this
 * function returns whether or not that field should be visible currently, based on the logic of its condition.
 *
 * @param condition The condition configured for the field.
 * @param conditionFieldDefinition The object defining the configuration of the *condition* field, i.e. the one that is
 * specified within the condition configured for the field, *not* the field for which we are determining whether or not
 * it should be visible currently.
 * @param conditionFieldCurrentValue The current value of the condition field.
 * @returns {boolean} Whether the field which has a condition specified should be visible currently.
 */
export function isFieldVisibleFromCondition(condition, conditionFieldDefinition, conditionFieldCurrentValue) {
  const { fieldValue: conditionValueToMatch } = condition;

  const conditionFieldType = conditionFieldDefinition.type;

  let currentValueMatchesConditionValue;

  if (isFunction(conditionValueToMatch)) {
    // If the type of fieldValue is a function then pass the current value of the condition field as parameter and call the function
    // to determine whether the field which has a condition specified should be visible currently
    currentValueMatchesConditionValue = conditionValueToMatch(conditionFieldCurrentValue);
  } else if (includes(FIELD_TYPES_ARRAY, conditionFieldType)) {
    let mode = get(conditionFieldDefinition, "mode", null);

    if (!mode) {
      mode = SELECT_FIELD_MODES.default;
    }

    if (mode === SELECT_FIELD_MODES.multiple) {
      // If the <Select> field has the `multiple` mode, then the field value will be an array, so we need to check
      // whether the condition's match value is one of the values in the condition field's current array value.
      currentValueMatchesConditionValue = includes(conditionFieldCurrentValue, conditionValueToMatch);
    } else {
      // Otherwise, the field value will be a primitive such as a string or number, so we can do a simple check to
      // see if the condition field's current value matches the condition's match value.
      currentValueMatchesConditionValue = conditionFieldCurrentValue === conditionValueToMatch;
    }
  } else {
    // For other field types, we just do the simple check described above.
    currentValueMatchesConditionValue = conditionFieldCurrentValue === conditionValueToMatch;
  }

  if (condition.type === CONDITION_TYPES.showOnMatch) {
    return currentValueMatchesConditionValue;
  }

  if (condition.type === CONDITION_TYPES.hideOnMatch) {
    return !currentValueMatchesConditionValue;
  }

  return true;
}

function convertTimeToViewFormat(timeStr) {
  const time = parseDateTime(timeStr, TIME_FORMAT);
  if (!time.isValid()) {
    return timeStr;
  }

  return time.format(VIEW_TIME_FORMAT);
}

function getDateTimeSchemaMessages(format) {
  switch (format) {
    case DATE_FORMAT:
    case DATE_TIME_FORMAT: {
      return {
        lessOrEqualDate: minVal => `Please select a date of ${minVal} or later.`,
        greaterOrEqualDate: maxVal => `Please select a date of ${maxVal} or earlier.`,
        outOfRange: (minVal, maxVal) => `Please select a date between ${minVal} and ${maxVal}.`,
        invalidRangeValues: (minVal, maxVal) => `Min Date (${minVal}) can't be greater than Max Date (${maxVal}).`,
      };
    }
    case TIME_FORMAT: {
      return {
        lessOrEqualDate: minVal => `Please select a time of ${convertTimeToViewFormat(minVal)} or later.`,
        greaterOrEqualDate: maxVal => `Please select a time of ${convertTimeToViewFormat(maxVal)} or earlier.`,
        outOfRange: (minVal, maxVal) =>
          `Please select a time between ${convertTimeToViewFormat(minVal)} and ${convertTimeToViewFormat(maxVal)}.`,
        invalidRangeValues: (minVal, maxVal) =>
          `Min Time (${convertTimeToViewFormat(minVal)}) can't be greater than Max Time (${convertTimeToViewFormat(
            maxVal,
          )}).`,
      };
    }
    default: {
      return {};
    }
  }
}

function schemaLengthLimit(fieldSchema, field, required) {
  const minLength = get(field, "minLength", null);
  const maxLength = get(field, "maxLength", null);

  const hasMinLength = isInteger(minLength) && minLength > 0;
  const hasMaxLength = isInteger(maxLength) && maxLength > 0;

  let schemaWithLengthLimit = fieldSchema.default("");

  if (!hasMinLength && !hasMaxLength) {
    return schemaWithLengthLimit.nullable();
  }

  let isValidParams = true;
  let message;

  if (hasMinLength && hasMaxLength) {
    if (minLength > maxLength) {
      isValidParams = false;
      console.error(`Min Length (${minLength}) can't be greater than Max Length (${maxLength}).`);
    } else {
      message = `Please enter a value of at least ${minLength} characters and at most ${maxLength} characters.`;
    }
  }

  if (!isValidParams) {
    return schemaWithLengthLimit.nullable();
  }

  if (hasMinLength) {
    message = message || `Please enter a value of ${minLength} characters or more.`;

    schemaWithLengthLimit = schemaWithLengthLimit.test("minLengthLimitBasedOnRequired", message, value => {
      const valueLength = value ? value.length : 0;

      // If the field is *not* required but it has a minimum length configured, we do not enforce this minimum length
      // if the user has not actually entered a value. This is to handle the scenario where a field has been configured
      // as not required (implying that an empty value is okay), but has also been configured with a minimum length of 1
      // or higher (implying that an empty value is not okay); in this scenario the "required" configuration and
      // "minimum length" configuration are contradicting each other and need to be reconciled in some way, which we do
      // by treating the "required" configuration as the more important one and assuming that if the field is not
      // configured as being required, then an empty value is okay, so we should ignore the "minimum length"
      // configuration if the value is empty.
      if (!required && valueLength === 0) {
        return true;
      }

      // Otherwise, we enforce the typical minimum length validation logic as per the field's configuration. Note that
      // this is intended to be functionally equivalent to using `yup.min(minLength, message)`.
      return valueLength >= minLength;
    });
  }

  if (hasMaxLength) {
    message = message || `Please enter a value of ${maxLength} characters or fewer.`;

    schemaWithLengthLimit = schemaWithLengthLimit.max(maxLength, message);
  }

  return schemaWithLengthLimit;
}

function schemaDateTimeRangeLimit(fieldSchema, field, format) {
  const minValue = get(field, "minValue", null);
  const maxValue = get(field, "maxValue", null);
  const hasMinValue = isString(minValue) && !isEmpty(minValue);
  const hasMaxValue = isString(maxValue) && !isEmpty(maxValue);

  if (hasMinValue || hasMaxValue) {
    const messages = getDateTimeSchemaMessages(format);
    let isValidParams = true;
    let msg;

    if (hasMinValue && hasMaxValue) {
      // TODO: Fix warning from moment when calling `isAfter` here.
      // The warning says: "Deprecation warning: value provided is not in a recognized RFC2822 or ISO format. moment
      // construction falls back to js Date(), which is not reliable across all browsers and versions." This appears to
      // be caused by the inputs (minValue, maxValue) being in a different format than DATE_TIME_FORMAT, which is
      // currently `"YYYY-MM-DD h:mm a"`. For now, moment seems to handle things correctly but it's possible there are
      // bugs related to this (maybe only in certain browsers).
      if (isAfter(minValue, maxValue, format)) {
        isValidParams = false;
        console.error(messages.invalidRangeValues(minValue, maxValue));
      } else {
        msg = messages.outOfRange(minValue, maxValue);
      }
    }

    if (isValidParams) {
      let schema = fieldSchema;

      if (hasMinValue) {
        schema = schema.test("lessOrEqualDateTime", msg || messages.lessOrEqualDate(minValue), dateTime => {
          if (isBlank(dateTime) || !isString(dateTime)) {
            return true;
          }

          return isSameOrAfter(dateTime, minValue, format);
        });
      }

      if (hasMaxValue) {
        schema = schema.test("greaterOrEqualDateTime", msg || messages.greaterOrEqualDate(maxValue), dateTime => {
          if (isBlank(dateTime) || !isString(dateTime)) {
            return true;
          }

          return isSameOrBefore(dateTime, maxValue, format);
        });
      }

      return schemaNullable(schema);
    }
  }

  return schemaNullable(fieldSchema);
}

function schemaWhiteList(fieldSchema, field) {
  const whiteList = get(field, "whitelist", null);

  if (isArray(whiteList) && !isEmpty(whiteList)) {
    let schema = fieldSchema;
    const type = get(field, "type", null);

    switch (type) {
      case FIELD_TYPES.country:
      case FIELD_TYPES.language: {
        let whiteListValues;

        if (type === FIELD_TYPES.country) {
          whiteListValues = whiteList.map(code => getName(code));
        } else {
          whiteListValues = whiteList.map(code => ISO6391.getName(code));
        }

        const whiteListText = whiteList.length === 1 ? "" : " one of the following values:";

        schema = schema.test("whiteList", `You must select${whiteListText} ${whiteListValues.join(", ")}.`, value => {
          let isValid = true;

          if (isArray(value)) {
            // eslint-disable-next-line consistent-return
            each(value, item => {
              if (!includes(whiteList, item)) {
                isValid = false;
                return false;
              }
            });
          } else if (isString(value) && !isEmpty(value) && !includes(whiteList, value)) {
            isValid = false;
          }

          return isValid;
        });
        break;
      }
      default: {
        console.error("Unsupported field type for whitelist.", type);
      }
    }

    return schema;
  }

  return fieldSchema;
}

function schemaBlackList(fieldSchema, field) {
  const blackList = get(field, "blacklist", null);

  if (isArray(blackList) && !isEmpty(blackList)) {
    let schema = fieldSchema;
    const type = get(field, "type", null);

    switch (type) {
      case FIELD_TYPES.country:
      case FIELD_TYPES.language: {
        let blackListValues;

        if (type === FIELD_TYPES.country) {
          blackListValues = blackList.map(code => getName(code));
        } else {
          blackListValues = blackList.map(code => ISO6391.getName(code));
        }

        const blackListText = blackList.length === 1 ? "value" : "following values:";

        schema = schema.test(
          "blackList",
          `You can't select the ${blackListText} ${blackListValues.join(", ")}.`,
          value => {
            let isValid = true;

            if (isArray(value)) {
              // eslint-disable-next-line consistent-return
              each(value, item => {
                if (includes(blackList, item)) {
                  isValid = false;
                  return false;
                }
              });
            } else if (isString(value) && !isEmpty(value) && includes(blackList, value)) {
              isValid = false;
            }

            return isValid;
          },
        );
        break;
      }
      default: {
        console.error("Unsupported field type for blacklist.", type);
      }
    }

    return schema;
  }

  return fieldSchema;
}

function schemaSelectType(field) {
  let mode = get(field, "mode", SELECT_FIELD_MODES.default);

  if (isEmpty(mode)) {
    mode = SELECT_FIELD_MODES.default;
  }

  /**
   * .default("") because the initial value of the select is "undefined" {@see selectUndefined}
   * A schema with any "strip" field removes all properties with a value of "undefined".
   * */
  if (mode === SELECT_FIELD_MODES.default) {
    return yup.string().default("");
  }

  return yup.array().default([]);
}

function schemaDecimalPlaces(fieldSchema, field) {
  let decimalPlaces = get(field, "decimalPlaces", null);

  if (!isBlank(decimalPlaces) && isString(decimalPlaces)) {
    decimalPlaces = toInteger(decimalPlaces);
  }

  if (!isInteger(decimalPlaces)) {
    return fieldSchema;
  }

  let schema = fieldSchema;

  if (decimalPlaces === 0) {
    schema = schema.integer("Please enter an integer (no decimal places).");
  } else {
    const placesText = decimalPlaces === 1 ? "place" : "places";

    schema = schema.test(
      "nonIntegerDecimalPlaces",
      `Please enter a value with a maximum of ${decimalPlaces} decimal ${placesText}.`,
      value => getDecimalsCount(value) <= decimalPlaces,
    );
  }

  return schema;
}

function schemaNumberLimit(fieldSchema, field) {
  let minValue = get(field, "minValue", null);
  let maxValue = get(field, "maxValue", null);

  if (!isBlank(minValue) && isString(minValue)) {
    minValue = toInteger(minValue);
  }

  if (!isBlank(maxValue) && isString(maxValue)) {
    maxValue = toInteger(maxValue);
  }

  const hasMinValue = isInteger(minValue);
  const hasMaxValue = isInteger(maxValue);

  if (!hasMinValue && !hasMaxValue) {
    return fieldSchema;
  }

  let isValidParams = true;
  let message;

  if (hasMinValue && hasMaxValue) {
    if (minValue > maxValue) {
      isValidParams = false;
      console.error(`Min Value (${minValue}) can't be greater than Max Value (${maxValue}).`);
    } else {
      message = `Please enter a value of at least ${minValue} and at most ${maxValue}.`;
    }
  }

  if (!isValidParams) {
    return fieldSchema;
  }

  let schema = fieldSchema;

  if (hasMinValue) {
    message = message || `Please enter a value of ${minValue} or higher.`;

    schema = schema.min(minValue, message);
  }

  if (hasMaxValue) {
    message = message || `Please enter a value of ${maxValue} or lower.`;

    schema = schema.max(maxValue, message);
  }

  return schema;
}

/**
 * Automatically generates a Yup schema based on the fields configuration.
 *
 * Note: if a schema is passed in, this is returned instead. This allows a custom schema to be used when needed
 * (instead of the auto-generated one).
 *
 * Note that logic needs to be kept in sync with rules enforced in `useInitialValuesFromFields` and
 * `mapFormFieldsToActionFormData`.
 */
// TODO refactor - this does not return 'schema from fields' if the schema param is returned
function useSchemaFromFields(schema, fields, validateRequired = true) {
  return useMemo(() => {
    if (schema) {
      return schema;
    }

    const fieldSchemas = {};

    each(Object.keys(fields), fieldId => {
      const field = fields[fieldId];
      const { type } = field;

      if (isNil(type)) {
        return;
      }

      if (includes(UPLOAD_TYPES, type)) {
        // Special handling for upload types. This was the previous behaviour and it appears to work correctly. Note
        // that the `src/components/FormFields/FormFieldFile.js` component has its own handling for validation logic, so
        // we don't need to apply the full validation logic here. Note also that regarding fields with conditions, the
        // field's own validation logic would only kick in when the field is actually being rendered, which will only be
        // when it's visible, thus the logic which makes the field visible or not ensures that upload fields will only
        // have their validation rules applied if they are visible.
        fieldSchemas[fieldId] = yup.array();

        return;
      }

      let required;

      if (!validateRequired) {
        // If the schema should not apply validation based on the `required` option configured for the field, we ignore
        // the configured value.
        required = false;
      } else {
        // Otherwise, we apply validation based on the value configured for the `required` option for the field.
        required = get(field, "required", false);
      }

      let fieldSchema;

      if (type === FIELD_TYPES.number) {
        fieldSchema = yup.number().typeError(TYPE_ERROR_MESSAGES[FIELD_TYPES.number]);
        fieldSchema = schemaDecimalPlaces(fieldSchema, field);
        fieldSchema = schemaNumberLimit(fieldSchema, field);

        if (!required) {
          fieldSchema = schemaNullable(fieldSchema);
        }
      } else if (type === FIELD_TYPES.bool) {
        fieldSchema = schemaNullable(yup.boolean());
      } else if (type === FIELD_TYPES.date) {
        fieldSchema = schemaDateTimeRangeLimit(yup.string().trim(), field, DATE_FORMAT);
      } else if (type === FIELD_TYPES.time) {
        fieldSchema = schemaDateTimeRangeLimit(yup.string().trim(), field, TIME_FORMAT);
      } else if (type === FIELD_TYPES.datetime) {
        fieldSchema = schemaDateTimeRangeLimit(yup.string().trim(), field, DATE_TIME_FORMAT);
      } else if (type === FIELD_TYPES.country || type === FIELD_TYPES.language) {
        fieldSchema = schemaSelectType(field);
        fieldSchema = schemaWhiteList(fieldSchema, field);
        fieldSchema = schemaBlackList(fieldSchema, field);
      } else if (type === FIELD_TYPES.select || type === FIELD_TYPES.group) {
        fieldSchema = schemaSelectType(field);
      } else if (type === FIELD_TYPES.email) {
        fieldSchema = yup
          .string()
          .trim()
          .email(TYPE_ERROR_MESSAGES[FIELD_TYPES.email])
          .default("");
      } else if (type === FIELD_TYPES.phone) {
        fieldSchema = schemaPhone();
      } else if (type === FIELD_TYPES.address) {
        fieldSchema = yup
          .mixed()
          .test("addressType", TYPE_ERROR_MESSAGES[FIELD_TYPES.address], value => {
            return isBlank(value) || isString(value) || isPlainObject(value);
          });
      } else if (type === FIELD_TYPES.content) {
        fieldSchema = yup.string().strip();
      } else if (includes(FIELD_TYPES_WITHOUT_META_CONFIG_VALUE, type)) {
        fieldSchema = yup.string().strip();
      } else {
        // String
        fieldSchema = schemaLengthLimit(yup.string().trim(), field, required);
      }

      if (required) {
        if (type === FIELD_TYPES.address) {
          fieldSchema = fieldSchema.test("addressRequired", REQUIRED_MESSAGES[type], value => !isBlank(value));
        } else {
          fieldSchema = fieldSchema.required(REQUIRED_MESSAGES[type]);
        }
      }

      const conditions = get(field, "conditions", []);

      if (!isEmpty(conditions)) {
        // TODO: When it's possible to add multiple conditions for a field, we need to add logic to check for all of them.
        // For now, fields are assumed to only ever have a single condition at the most.
        const condition = conditions[0];
        const conditionFieldId = condition.field;

        fieldSchema = fieldSchema.when(conditionFieldId, (conditionFieldCurrentValue, fieldCurrentSchema) => {
          const isFieldVisible = isFieldVisibleFromCondition(
            condition,
            fields[conditionFieldId],
            conditionFieldCurrentValue,
          );

          // If the field is not currently visible based on its conditions, we ignore its normal schema (as defined
          // above) and return a schema that will not enforce any validation logic. We use `yup.mixed()` for this as
          // it's the base schema that matches all types. See https://github.com/jquense/yup#mixed.
          if (!isFieldVisible) {
            return yup.mixed();
          }

          // Otherwise, if the field is currently visible based on its conditions, we just use the field's schema as
          // defined by all the rules/logic above.
          return fieldCurrentSchema;
        });
      }

      fieldSchemas[fieldId] = fieldSchema;
    });

    if (isEmpty(fieldSchemas)) {
      return undefined;
    }

    return yup.object().shape(fieldSchemas);
  }, [schema, fields, validateRequired]);
}

export default useSchemaFromFields;
